/**
 * Fallback de permissão no front: garante que eventos de ticket recebidos via socket
 * (enquanto o backend ainda puder emitir em namespace amplo) só sejam exibidos/inseridos
 * quando o usuário atual tiver permissão.
 * Pode ser removido quando todos os emits do backend forem por rooms (user/queue/company-all).
 *
 * @param {Object} currentUser - usuário logado (id, profile, allUserChat, whatsappId, queues)
 * @param {Object} ticket - ticket do evento (id, queueId, userId, status, whatsappId, ...)
 * @param {number[]} [allowedQueueIds] - IDs das filas permitidas (ex.: user.queues.map(q => q.id))
 * @param {number[]} [allowedWhatsappIds] - IDs das conexões permitidas para o usuário
 * @returns {boolean} true se o usuário pode ver o ticket
 */
export function canSeeTicket(
  currentUser,
  ticket,
  allowedQueueIds = null,
  allowedWhatsappIds = null
) {
  if (!currentUser || !ticket) return false;

  const permissions = Array.isArray(currentUser.permissions) ? currentUser.permissions : [];
  // God mode (owner/super) ou quem tem tickets:read_all vê todos. Admin comum respeita RBAC.
  const canSeeAllUsers =
    currentUser.super || currentUser.isOwner || permissions.includes('tickets:read_all');
  const canSeeGroups =
    currentUser.super || currentUser.isOwner || permissions.includes('tickets:read_groups');
  // tickets:read_not_in_queue: vê todos os tickets sem fila (de qualquer atendente).
  const canSeeUnqueued =
    currentUser.super || currentUser.isOwner || permissions.includes('tickets:read_not_in_queue');
  const queueIds =
    Array.isArray(allowedQueueIds) && allowedQueueIds.length > 0
      ? allowedQueueIds
      : currentUser.queues?.map((q) => q.id) || [];
  const whatsappIds = Array.isArray(allowedWhatsappIds) ? allowedWhatsappIds : [];

  // Super-admin e owner: aceitar sempre, sem restrição de conexão.
  if (currentUser.super || currentUser.isOwner) {
    return true;
  }

  // Escopo de conexão: se o usuário tem lista de whatsapps, respeitar sempre.
  if (
    whatsappIds.length > 0 &&
    ticket.whatsappId != null &&
    !whatsappIds.includes(ticket.whatsappId)
  ) {
    return false;
  }

  // tickets:read_all com escopo de conexão já validado acima.
  if (canSeeAllUsers) {
    return true;
  }

  // groups: ticket de grupo não depende de fila/usuário, só da permissão de grupos.
  if ((ticket.status || '').toLowerCase() === 'group' || ticket.isGroup === true) {
    return canSeeGroups;
  }

  // tickets:read_not_in_queue: vê qualquer ticket sem fila (inclusive automação).
  if (ticket.queueId == null && canSeeUnqueued) {
    return true;
  }

  // Perfil user: aplicar regras de restrição
  // Automação sem fila nem usuário: ignorar
  if (ticket.queueId == null && ticket.userId == null) {
    return false;
  }

  // Ticket explicitamente atribuído ao usuário atual: permitir sempre.
  // Esta checagem vem antes da restrição de fila para cobrir transferências diretas.
  if (ticket.userId != null && ticket.userId === currentUser.id) {
    return true;
  }

  // Filas: se o user tem filas, o ticket deve estar em uma delas
  if (queueIds.length > 0 && ticket.queueId != null) {
    if (!queueIds.includes(ticket.queueId)) {
      return false;
    }
  }

  const status = (ticket.status || '').toLowerCase();
  const ticketUserId = ticket.userId;

  if (status === 'open' || status === 'closed') {
    return ticketUserId === currentUser.id;
  }

  if (status === 'pending') {
    return ticketUserId == null || ticketUserId === currentUser.id;
  }

  // Outros status (ex.: group, lgpd, nps): permitir se for do usuário ou sem dono
  return ticketUserId == null || ticketUserId === currentUser.id;
}

/**
 * Determina se o usuário deve receber notificação (som + desktop) para um ticket.
 * Mais restrito que canSeeTicket: admin vê tudo mas só é notificado do que é dele.
 *
 * Regras:
 * - Ticket com userId → só notifica o usuário atribuído
 * - Ticket só com queueId → notifica quem pertence à fila
 * - Sem fila nem usuário → notifica apenas admin (ticket ainda não triado)
 *
 * @param {Object} currentUser - usuário logado
 * @param {Object} ticket - ticket do evento
 * @returns {boolean}
 */
export function shouldNotifyForTicket(currentUser, ticket) {
  if (!currentUser || !ticket) return false;

  const userQueues = currentUser.queues?.map((q) => q.id) || [];
  const permissions = Array.isArray(currentUser.permissions) ? currentUser.permissions : [];
  // Quem triagem tickets não atribuídos: owner/super, tickets:read_all ou read_not_in_queue.
  const canSeeUntriaged =
    Boolean(currentUser.super) ||
    Boolean(currentUser.isOwner) ||
    permissions.includes('tickets:read_all') ||
    permissions.includes('tickets:read_not_in_queue');

  // Ticket atribuído a um usuário específico → só esse usuário é notificado
  if (ticket.userId != null) {
    return ticket.userId === currentUser.id;
  }

  // Ticket só com fila → notifica quem pertence à fila
  if (ticket.queueId != null) {
    return userQueues.includes(ticket.queueId);
  }

  // Sem fila nem usuário → notifica apenas quem pode triar (ticket ainda não triado)
  return canSeeUntriaged;
}
